import React, { useState, useContext } from "react";
import {
  Page,
  Layout,
  Card,
  FormLayout,
  Checkbox,
  Select,
  Text,
  Stack,
  Banner,
  Icon,
  Tooltip,
  RadioButton,
  Button,
  ButtonGroup,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { TitleBar } from "@shopify/app-bridge-react";
import { AlertMinor, LockMinor, ExternalMinor } from "@shopify/polaris-icons";
import { AppContext } from "../../../../components/global/appContext";
import { useUpdateLanguageSettings } from "../../../../hooks/queries/languageSettings";
import useCompare from "../../../../hooks/useCompare";
import { mobileTitle } from "../../../../utilities/mobile";

export default function LanguageSettingsForm({ initialLanguageSettings }) {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  const [languageSettings, setLanguageSettings] = useState(initialLanguageSettings);
  
  // Keep a reference to the initial settings that gets updated on successful save
  const [currentInitialSettings, setCurrentInitialSettings] = useState(initialLanguageSettings);

  const hasUnsavedChanges = !useCompare(languageSettings, currentInitialSettings);

  // Check plan access - should read from initial settings since plan access doesn't change during session
  const planAccess = initialLanguageSettings?.plan_access || {};
  const canEnableMultipleLanguages = planAccess.can_enable_multiple_languages;
  const upgradeMessage = planAccess.upgrade_message || "Upgrade to Superest for multi-language support";


  // DEBUG: Add debugging to see what's happening
  console.log('DEBUG: initialLanguageSettings:', initialLanguageSettings);
  console.log('DEBUG: planAccess:', planAccess);
  console.log('DEBUG: canEnableMultipleLanguages:', canEnableMultipleLanguages);

  // Get the shop's actual default locale from initial settings
  const shopDefaultLocale = initialLanguageSettings?.localization?.default_locale || 'en';

  const updateLanguageSettingsMutation = useUpdateLanguageSettings({
    onSuccess: (data) => {
      setLanguageSettings(data);
      setCurrentInitialSettings(data); // Update the reference for comparison
      appContext.setMessage(t("settings.languages.messages.savedSuccessfully"));
    },
    onError: (error) => {
      setLanguageSettings(error.response?.data || languageSettings);
      appContext.setMessage(t("settings.languages.messages.couldntSave"), true);
    },
  });

  function handleUpdate(key, value) {
    setLanguageSettings((oldSettings) => ({
      ...oldSettings,
      localization: { ...oldSettings.localization, [key]: value },
    }));
  }

  function handleLocaleToggle(localeCode, enabled) {
    const currentSupportedLocales = languageSettings.localization?.supported_locales || [shopDefaultLocale];
    const currentDefaultLocale = languageSettings.localization?.default_locale || shopDefaultLocale;
    let newSupportedLocales;

    if (enabled) {
      newSupportedLocales = [...currentSupportedLocales, localeCode];
    } else {
      // Don't allow unchecking the default language
      if (localeCode === currentDefaultLocale) {
        return; // Do nothing if trying to uncheck the default language
      }
      newSupportedLocales = currentSupportedLocales.filter(code => code !== localeCode);
    }

    // Ensure default language is always in supported locales
    if (!newSupportedLocales.includes(currentDefaultLocale)) {
      newSupportedLocales.push(currentDefaultLocale);
    }

    // Build enabled_languages format from supported_locales and current enabled_languages
    const currentEnabledLanguages = languageSettings.localization?.enabled_languages || {};
    const newEnabledLanguages = {};
    newSupportedLocales.forEach(locale => {
      // If this is a newly added locale, default shopify_enabled to true
      if (currentEnabledLanguages[locale]) {
        // Keep existing settings for already configured locales
        newEnabledLanguages[locale] = currentEnabledLanguages[locale];
      } else {
        // Default to true for newly added locales
        newEnabledLanguages[locale] = {
          shopify_enabled: true
        };
      }
    });

    // Update both supported_locales and enabled_languages
    setLanguageSettings((oldSettings) => ({
      ...oldSettings,
      localization: { 
        ...oldSettings.localization, 
        supported_locales: newSupportedLocales,
        enabled_languages: newEnabledLanguages
      },
    }));
  }

  function handleDefaultLocaleChange(newDefaultLocale) {
    const currentSupportedLocales = languageSettings.localization?.supported_locales || [shopDefaultLocale];
    
    // Ensure the new default locale is in supported locales
    let newSupportedLocales = [...currentSupportedLocales];
    if (!newSupportedLocales.includes(newDefaultLocale)) {
      newSupportedLocales.push(newDefaultLocale);
    }

    // Update both default locale and supported locales
    setLanguageSettings((oldSettings) => ({
      ...oldSettings,
      localization: { 
        ...oldSettings.localization, 
        default_locale: newDefaultLocale,
        supported_locales: newSupportedLocales
      },
    }));
  }

  function handleLanguageSettingChange(localeCode, key, value) {
    setLanguageSettings((oldSettings) => {
      const currentSupportedLocales = oldSettings.localization?.supported_locales || [shopDefaultLocale];
      const currentDefaultLocale = oldSettings.localization?.default_locale || shopDefaultLocale;
      let newSupportedLocales = [...currentSupportedLocales];
      
      // If enabling shopify integration, automatically enable the language
      if (key === 'shopify_enabled' && value === true) {
        if (!newSupportedLocales.includes(localeCode)) {
          newSupportedLocales.push(localeCode);
        }
      }
      
      // Update enabled_languages format
      const currentEnabledLanguages = oldSettings.localization?.enabled_languages || {};
      
      // Build new enabled_languages from supported_locales
      const newEnabledLanguages = {};
      newSupportedLocales.forEach(locale => {
        if (locale === localeCode) {
          // Update the changed locale
          newEnabledLanguages[locale] = {
            ...(currentEnabledLanguages[locale] || {}),
            [key]: value
          };
        } else {
          // Keep existing settings for other locales
          newEnabledLanguages[locale] = currentEnabledLanguages[locale] || {
            shopify_enabled: false
          };
        }
      });
      
      return {
        ...oldSettings,
        localization: {
          ...oldSettings.localization,
          supported_locales: newSupportedLocales,
          enabled_languages: newEnabledLanguages
        }
      };
    });
  }

  // Helper function to generate preview URL for a locale
  function generatePreviewUrl(localeCode) {
    // Use the backend-generated preview URLs from the saved settings
    const previewUrls = initialLanguageSettings.shop?.preview_urls || {};
    return previewUrls[localeCode] || `${initialLanguageSettings.shop?.active_domain || ''}${initialLanguageSettings.shop?.proxy_path || '/tools/locations'}`;
  }

  // Handler for preview button click
  function handlePreviewClick(localeCode) {
    const previewUrl = generatePreviewUrl(localeCode);
    window.open(previewUrl, '_blank');
  }

  const { errors, available_locales = [] } = languageSettings;
  const localization = languageSettings.localization || {};
  const { enabled = false, default_locale = shopDefaultLocale, supported_locales = [shopDefaultLocale], enabled_languages = {} } = localization;

  const formatErrorFor = (key) => {
    if (errors && errors[key]) {
      const errorMessage = errors[key].join(" & ");
      return errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
    }
  };

  // Default language can be selected from all available languages
  const defaultLocaleOptions = available_locales.map(locale => ({
    label: locale.name,
    value: locale.code
  }));

  // Helper function to get URL format example
  const getUrlFormatExample = (localeCode, shopifyEnabled) => {
    if (shopifyEnabled) {
      return {
        format: 'path',
        example: `/${localeCode}/tools/locations/location-name`,
        description: 'Clean URLs (recommended)',
        status: 'enabled'
      };
    } else {
      return {
        format: 'query',
        example: `/tools/locations/location-name?locale=${localeCode}`,
        description: 'Query parameter URLs',
        status: 'fallback'
      };
    }
  };

  // Handle form submission (triggered by Save Bar)
  const handleSubmit = (e) => {
    e.preventDefault();
    updateLanguageSettingsMutation.mutate(languageSettings.localization);
  };

  // Handle form reset (triggered by Discard button in Save Bar)
  const handleReset = (e) => {
    e.preventDefault();
    setLanguageSettings(currentInitialSettings);
  };

  return (
    <form 
      data-save-bar 
      onSubmit={handleSubmit}
      onReset={handleReset}
    >
    <Page>
      <TitleBar
        title={mobileTitle(t("settings.languages.title"), t("settings.common.breadcrumbs.settings"))}
        breadcrumbs={[{ content: t("settings.common.breadcrumbs.settings") }]}
      />

      <Layout>
        {!canEnableMultipleLanguages && (
          <Layout.Section>
            <Banner
              title={t("settings.languages.banner.upgradeTitle")}
              status="warning"
              action={{
                content: t("settings.languages.banner.upgradeAction"),
                url: "/settings/plan/edit"
              }}
            >
              <Stack vertical spacing="tight">
                <Text as="p">
                  {upgradeMessage}. {t("settings.languages.banner.upgradeMessage")}
                </Text>
                <Stack alignment="center" spacing="tight">
                  <Icon source={LockMinor} tone="subdued" />
                  <Text as="p" tone="subdued">
                    {t("settings.languages.banner.disabledMessage")}
                  </Text>
                </Stack>
              </Stack>
            </Banner>
          </Layout.Section>
        )}

        <Layout.AnnotatedSection
          title={t("settings.languages.defaultLanguage.title")}
          description={t("settings.languages.defaultLanguage.description")}
        >
          <Card sectioned>
            <FormLayout>
              <Select
                label={t("settings.languages.defaultLanguage.label")}
                options={defaultLocaleOptions}
                onChange={handleDefaultLocaleChange}
                value={default_locale}
                error={formatErrorFor("default_locale")}
              />
            </FormLayout>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title={t("settings.languages.internationalization.title")}
          description={t("settings.languages.internationalization.description")}
        >
          <Card sectioned>
            <FormLayout>
              <Checkbox
                label={t("settings.languages.internationalization.enableLabel")}
                checked={enabled}
                onChange={canEnableMultipleLanguages ? (value) => handleUpdate("enabled", value) : undefined}
                helpText={t("settings.languages.internationalization.enableHelp")}
                error={formatErrorFor("enabled")}
                disabled={!canEnableMultipleLanguages}
              />
            </FormLayout>
          </Card>
        </Layout.AnnotatedSection>

        {enabled && (
          <>
            <Layout.AnnotatedSection
              title={t("settings.languages.supportedLanguages.title")}
              description={t("settings.languages.supportedLanguages.description")}
            >
              <Card sectioned>
                <FormLayout>
                  <Stack vertical>
                    {available_locales.map((locale) => {
                      const isDefaultLocale = locale.code === default_locale;
                      const isSupported = supported_locales.includes(locale.code);
                      const localeSettings = enabled_languages[locale.code] || {};
                      // Default to true for new languages, otherwise use existing value
                      const shopifyEnabled = localeSettings.shopify_enabled !== undefined 
                        ? localeSettings.shopify_enabled 
                        : true;
                      const urlInfo = getUrlFormatExample(locale.code, shopifyEnabled);
                      
                      // Check if the language is actually saved/enabled in the backend
                      const initialSupportedLocales = initialLanguageSettings.localization?.supported_locales || [shopDefaultLocale];
                      const isActuallySaved = initialSupportedLocales.includes(locale.code);
                      
                      return (
                        <div key={locale.code}>
                          <Stack alignment="center" spacing="tight">
                            <Checkbox
                              label={`${locale.name} (${locale.code})`}
                              checked={isSupported}
                              onChange={canEnableMultipleLanguages ? (value) => handleLocaleToggle(locale.code, value) : undefined}
                              disabled={isDefaultLocale || !canEnableMultipleLanguages} // Default language is always enabled OR no feature access
                            />
                            {!locale.fully_supported && (
                              <Tooltip content={t("settings.languages.supportedLanguages.tooltips.noTemplates")}>
                                <Icon source={AlertMinor} tone="warning" />
                              </Tooltip>
                            )}
                            {isActuallySaved && (
                              <Button
                                icon={ExternalMinor}
                                size="slim"
                                onClick={() => handlePreviewClick(locale.code)}
                                accessibilityLabel={t("settings.languages.supportedLanguages.preview.tooltip", { language: locale.name })}
                              >
                                {t("settings.languages.supportedLanguages.preview.button")}
                              </Button>
                            )}
                          </Stack>
                          {isDefaultLocale && (
                            <Text variant="bodySm" tone="subdued" as="p">
                              {t("settings.languages.supportedLanguages.defaultLocaleNote")}
                            </Text>
                          )}
                          
                          {/* Show Shopify integration options for supported languages (except default) */}
                          {isSupported && !isDefaultLocale && (
                            <div style={{ marginLeft: '2rem', marginTop: '0.5rem' }}>
                              <Card sectioned>
                                <Stack vertical spacing="tight">
                                  <Text variant="headingSm" as="h4">
                                  </Text>
                                  
                                  <Checkbox
                                    label={t("settings.languages.supportedLanguages.shopifyEnabled.label")}
                                    checked={shopifyEnabled === true}
                                    onChange={canEnableMultipleLanguages ? (value) => handleLanguageSettingChange(locale.code, 'shopify_enabled', value) : undefined}
                                    disabled={!canEnableMultipleLanguages}
                                  />
                                  
                                  <Text variant="bodySm" tone="subdued" as="p">
                                   <span style={{fontWeight: 'bold'}}>{t("settings.languages.supportedLanguages.shopifyEnabled.importantNote")}</span> {t("settings.languages.supportedLanguages.shopifyEnabled.description")}
                                  </Text>
                                  
                                  {shopifyEnabled === true ? (
                                    <Stack vertical spacing="extraTight">
                                      <Text variant="bodySm" tone="success" as="span">
                                        ✓ {t("settings.languages.supportedLanguages.urlFormats.cleanUrls")} {getUrlFormatExample(locale.code, true).example}
                                      </Text>
                                      <Text variant="bodySm" tone="success" as="span">
                                        ✓ {t("settings.languages.supportedLanguages.urlFormats.betterSeo")}
                                      </Text>
                                    </Stack>
                                  ) : (
                                    <Stack vertical spacing="extraTight">
                                      <Text variant="bodySm" tone="warning" as="span">
                                        ⚠️ {t("settings.languages.supportedLanguages.urlFormats.queryUrls")} {getUrlFormatExample(locale.code, false).example}
                                      </Text>
                                      <Text variant="bodySm" tone="warning" as="span">
                                        {t("settings.languages.supportedLanguages.urlFormats.considerEnabling")}
                                      </Text>
                                    </Stack>
                                  )}
                                </Stack>
                              </Card>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </Stack>
                </FormLayout>
              </Card>
            </Layout.AnnotatedSection>
          </>
        )}
      </Layout>
    </Page>
    </form>
  );
} 